import { build_policy_drawer } from './compliance_policies_drawers';
import {
  create_empty_exception_form,
  create_empty_policy_form,
  create_exception_form_from_row,
  create_policy_form_from_row,
  initial_exception_sequence,
  initial_policy_sequence,
  normalize_policy_approval_stage,
  resolve_exception_status_class,
  resolve_policy_review_class,
  resolve_policy_status_class
} from './compliance_policies_helpers';
import { upsert_modal_row } from './compliance_policies_ui_helpers';

let policy_row_seq = initial_policy_sequence;
let exception_row_seq = initial_exception_sequence;

function value_or_default(value, fallback_value) {
  const resolved_value = value || fallback_value;
  return resolved_value;
}

function modal_index_from_row(rows, row, index) {
  if (typeof index === 'number') return index;
  if (!row) return null;
  const resolved_index = rows.findIndex((item) => item.id === row.id);
  return resolved_index;
}

function next_exception_id(context) {
  const exception_row = context.exception_rows[context.exception_modal_index];
  if (context.exception_modal_mode === 'edit' && context.exception_modal_index !== null && exception_row) {
    return exception_row.id;
  }
  exception_row_seq += 1;
  return `exception-${exception_row_seq}`;
}

function next_policy_id(context) {
  const policy_row = context.register_rows[context.policy_modal_index];
  if (context.policy_modal_mode === 'edit' && context.policy_modal_index !== null && policy_row) {
    return policy_row.id;
  }
  policy_row_seq += 1;
  return `policy-${policy_row_seq}`;
}

function exception_row_payload(context) {
  const form = context.exception_modal_form || create_empty_exception_form();
  const status_label = value_or_default(form.status, 'Approved');
  const requested_by = value_or_default(form.requested_by, 'TBC');
  const approved_by = value_or_default(form.approved_by, 'TBC');
  const row = {
    id: next_exception_id(context),
    exception: value_or_default(form.exception.trim(), 'Untitled exception'),
    policy: value_or_default(form.policy, 'Procurement policy'),
    requested_by,
    approved_by,
    expires: value_or_default(form.expires, 'Pending'),
    status: status_label,
    status_class: resolve_exception_status_class(status_label)
  };
  return row;
}

function policy_review_value(form) {
  const review_value = form.next_review_date || (form.review_status === 'Not issued' ? 'Not issued' : 'Pending');
  return review_value;
}

function policy_row_payload(context) {
  const form = context.policy_modal_form || create_empty_policy_form();
  const status_label = value_or_default(form.status, 'Draft');
  const review = policy_review_value(form);
  const review_status = value_or_default(form.review_status, 'Scheduled');
  const row = {
    id: next_policy_id(context),
    policy: value_or_default(form.policy.trim(), 'Untitled policy'),
    category: value_or_default(form.category, 'Finance'),
    version: value_or_default(form.version, 'v1'),
    owner: value_or_default(form.owner, 'TBC'),
    approver: value_or_default(form.approver, 'Board'),
    status: status_label,
    status_class: resolve_policy_status_class(status_label),
    acknowledged: null,
    ack_tone: '#c7651f',
    controls: 0,
    review,
    review_class: resolve_policy_review_class(review_status),
    approval_stage: value_or_default(form.approval_stage, '0 - Owner draft'),
    board_approval_required: value_or_default(form.board_approval_required, 'Yes'),
    review_status,
    next_review_date: value_or_default(form.next_review_date, ''),
    effective_date: value_or_default(form.effective_date, ''),
    summary: value_or_default(form.summary, '')
  };
  return row;
}

export const policies_computed = {
  active_policy() {
    return this.register_rows.find((row) => row.id === this.active_policy_ref) || null;
  },
  category_cards_view() {
    return this.category_cards.map((card) => {
      const policies = this.register_rows.filter((row) => row.category === card.title);
      const approved_count = policies.filter((row) => row.status === 'Approved').length;
      return {
        ...card,
        policy_count: policies.length,
        approved_count,
        subtitle: `${policies.length} ${policies.length === 1 ? 'policy' : 'policies'} - ${approved_count} approved`
      };
    });
  },
  filtered_category_rows() {
    return this.register_rows.filter((row) => row.category === this.active_category_filter);
  },
  active_policy_drawer() {
    return build_policy_drawer(this.active_policy);
  },
  any_modal_open() {
    return this.policy_drawer_open || this.policy_modal_open || this.exception_modal_open;
  }
};

export const policies_methods = {
  set_tab(tab_id) {
    this.active_tab = tab_id;
    this.close_policy_drawer();
    this.close_policy_modal();
    this.close_exception_modal();
    if (tab_id === 'categories' && !this.active_category_filter && this.category_cards.length) {
      this.active_category_filter = this.category_cards[0].title;
    }
  },
  set_category_filter(category) {
    this.active_category_filter = category;
  },
  open_policy_drawer(row) {
    if (!row) return;
    this.active_policy_ref = row.id;
    this.policy_drawer_open = true;
  },
  open_policy_drawer_by_policy(policy_name) {
    const row = this.register_rows.find((item) => item.policy === policy_name);
    if (row) this.open_policy_drawer(row);
  },
  close_policy_drawer() {
    this.active_policy_ref = null;
    this.policy_drawer_open = false;
  },
  open_policy_modal(row = null, index = null) {
    const resolved_index = modal_index_from_row(this.register_rows, row, index);
    this.policy_modal_mode = row ? 'edit' : 'create';
    this.policy_modal_index = resolved_index;
    this.policy_modal_form = row ? create_policy_form_from_row(row) : create_empty_policy_form();
    this.policy_modal_form.approval_stage = normalize_policy_approval_stage(this.policy_modal_form.approval_stage, this.policy_approval_stage_options);
    this.policy_modal_open = true;
  },
  close_policy_modal() {
    this.policy_modal_open = false;
    this.policy_modal_mode = 'create';
    this.policy_modal_index = null;
  },
  open_exception_modal(row = null, index = null) {
    const resolved_index = modal_index_from_row(this.exception_rows, row, index);
    this.exception_modal_mode = row ? 'edit' : 'create';
    this.exception_modal_index = resolved_index;
    this.exception_modal_form = row ? create_exception_form_from_row(row) : create_empty_exception_form();
    this.exception_modal_open = true;
  },
  close_exception_modal() {
    this.exception_modal_open = false;
    this.exception_modal_mode = 'create';
    this.exception_modal_index = null;
  },
  save_exception_modal() {
    const row = exception_row_payload(this);
    upsert_modal_row(this, 'exception_rows', 'exception_modal_index', row);
    this.close_exception_modal();
  },
  delete_exception_modal() {
    if (this.exception_modal_mode !== 'edit' || this.exception_modal_index === null) return;
    this.exception_rows.splice(this.exception_modal_index, 1);
    this.close_exception_modal();
  },
  save_policy_modal() {
    const row = policy_row_payload(this);
    const existing_row = Reflect.get(this.register_rows, this.policy_modal_index) || {};
    const is_edit = this.policy_modal_mode === 'edit' && this.policy_modal_index !== null;
    const saved_row = is_edit
      ? upsert_modal_row(this, 'register_rows', 'policy_modal_index', { ...existing_row, ...row })
      : upsert_modal_row(this, 'register_rows', 'policy_modal_index', row);
    this.open_policy_drawer(saved_row);
    this.close_policy_modal();
  }
};

export const policies_mounted = function policies_mounted() {
  if (this.any_modal_open && typeof document !== 'undefined') {
    document.body.classList.add('policies-overlay-open');
  }
};

export const policies_before_destroy = function policies_before_destroy() {
  if (typeof document !== 'undefined') {
    document.body.classList.remove('policies-overlay-open');
  }
};

export const policies_watch = {
  any_modal_open(is_open) {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('policies-overlay-open', is_open);
  }
};
